if (!customElements.get("bundle-product-block")) {
  customElements.define(
    "bundle-product-block",
    class BundleProductBlock extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector("[data-bundle-add]");
        this.errorWrapper = this.querySelector("[data-bundle-error]");
        this.items = this.querySelectorAll("[data-bundle-item]");
        this.productData = {};
        this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
      }

      formatMoney(cents, format) {
        const amount = (cents / 100).toFixed(2);
        return format
          .replace("{{amount_with_comma_separator}}", amount.replace(".", ","))
          .replace("{{amount_no_decimals_with_comma_separator}}", Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","))
          .replace("{{amount_no_decimals}}", Math.round(cents / 100))
          .replace("{{amount}}", amount);
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.items.forEach((item) => {
          const productId = item.dataset.productId;
          const jsonEl = item.querySelector("[data-variants-json]");
          if (jsonEl) {
            const data = JSON.parse(jsonEl.textContent);
            this.productData[productId] = {
              element: item,
              optionNames: data.options,
              variants: data.variants,
            };
          }
        });

        this.addEventListener("change", this.onOptionChange.bind(this));

        if (this.button) {
          this.button.addEventListener("click", this.onAddToCart.bind(this));
        }

        this.initMainPickerSync();
      }

      initMainPickerSync() {
        const productInfo = this.closest("product-info");
        if (!productInfo) return;

        productInfo.addEventListener("change", (event) => {
          if (event.target.closest("bundle-product-block")) return;

          const field = event.target.closest("[data-option-name]");
          if (!field) return;

          const optionName = field.dataset.optionName;
          const newValue = event.target.value;

          if (!newValue) return;
          this.syncOptionToItems(optionName, newValue);
        });
      }

      syncOptionToItems(optionName, value) {
        this.items.forEach((item) => {
          let changed = false;

          const radios = item.querySelectorAll(
            `input[type="radio"][data-option-name="${optionName}"]`
          );
          radios.forEach((radio) => {
            if (radio.value === value) {
              radio.checked = true;
              changed = true;
            }
          });

          if (!changed) {
            const select = item.querySelector(
              `select[data-option-name="${optionName}"]`
            );
            if (select) {
              for (const opt of select.options) {
                if (opt.value === value) {
                  select.value = value;
                  changed = true;
                  break;
                }
              }
            }
          }

          if (changed) {
            const productId = item.dataset.productId;
            const data = this.productData[productId];
            if (!data) return;

            const selectedOptions = this.getSelectedOptions(item);
            const variant = this.findVariant(data, selectedOptions);
            if (variant) {
              this.updatePrice(item, variant);
              this.updateImage(item, variant);
            }
            this.updateSelectedLabels(item);
          }
        });

        this.updateButtonTotal();
      }

      onOptionChange(event) {
        const item = event.target.closest("[data-bundle-item]");
        if (!item) return;

        const productId = item.dataset.productId;
        const data = this.productData[productId];
        if (!data) return;

        const selectedOptions = this.getSelectedOptions(item);
        const variant = this.findVariant(data, selectedOptions);

        if (variant) {
          this.updatePrice(item, variant);
          this.updateImage(item, variant);
        }

        this.updateSelectedLabels(item);
        this.updateButtonTotal();
      }

      getSelectedOptions(item) {
        const options = {};
        item
          .querySelectorAll('input[type="radio"]:checked')
          .forEach((input) => {
            options[input.dataset.optionName] = input.value;
          });
        item
          .querySelectorAll("select[data-option-name]")
          .forEach((select) => {
            options[select.dataset.optionName] = select.value;
          });
        return options;
      }

      findVariant(data, selectedOptions) {
        return data.variants.find((variant) =>
          data.optionNames.every(
            (name, i) => variant[`option${i + 1}`] === selectedOptions[name]
          )
        );
      }

      updatePrice(item, variant) {
        const wrapper = item.querySelector("[data-bundle-item-price]");
        if (!wrapper) return;

        const fmt = this.moneyFormat;

        const saleEl = wrapper.querySelector("[data-sale-price]");
        if (saleEl) saleEl.innerHTML = this.formatMoney(variant.price, fmt);

        let compareEl = wrapper.querySelector("[data-compare-price]");
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (!compareEl) {
            compareEl = document.createElement("s");
            compareEl.className = "bundle-block__compare-price";
            compareEl.setAttribute("data-compare-price", "");
            wrapper.prepend(compareEl);
          }
          compareEl.innerHTML = this.formatMoney(variant.compare_at_price, fmt);
          compareEl.style.display = "";
        } else if (compareEl) {
          compareEl.style.display = "none";
        }
      }

      updateImage(item, variant) {
        if (!variant.featured_image) return;
        const img = item.querySelector("[data-bundle-item-image]");
        if (!img) return;

        const src = variant.featured_image.src;
        const maxW = variant.featured_image.width;
        img.srcset = this.buildSrcset(src, maxW);
        img.src = this.sizedUrl(src, 360);
      }

      sizedUrl(url, width) {
        if (url.includes("?")) return `${url}&width=${width}`;
        return `${url}?width=${width}`;
      }

      buildSrcset(url, maxWidth) {
        const widths = [165, 360, 533, 720];
        return widths
          .filter((w) => w <= maxWidth)
          .map((w) => `${this.sizedUrl(url, w)} ${w}w`)
          .concat([`${url} ${maxWidth}w`])
          .join(", ");
      }

      updateSelectedLabels(item) {
        item.querySelectorAll("[data-selected-value]").forEach((label) => {
          const name = label.dataset.selectedValue;
          const radio = item.querySelector(
            `input[data-option-name="${name}"]:checked`
          );
          const select = item.querySelector(
            `select[data-option-name="${name}"]`
          );
          if (radio) label.textContent = radio.value;
          else if (select) label.textContent = select.value;
        });
      }

      updateButtonTotal() {
        // Button no longer shows aggregate price — no-op
      }

      onAddToCart(event) {
        event.preventDefault();

        this.button.classList.add("loading");
        this.button.setAttribute("disabled", "true");

        const items = [];
        const sortedItems = Array.from(this.items).sort(
          (a, b) =>
            parseInt(a.dataset.bundleIndex) - parseInt(b.dataset.bundleIndex)
        );
        sortedItems.forEach((item) => {
          const productId = item.dataset.productId;
          const data = this.productData[productId];
          if (!data) return;

          const opts = this.getSelectedOptions(item);
          const variant = this.findVariant(data, opts);
          if (!variant) return;

          items.push({ id: variant.id, quantity: 1 });
        });

        if (items.length === 0) return;

        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ items }),
        })
          .then((r) => r.json())
          .then((response) => {
            if (response.status) {
              this.showNotification(response.description, "error");
              return;
            }

            this.showNotification("Added to cart!", "success");

            fetch("/cart.js")
              .then((r) => r.json())
              .then((cart) => {
                const countEls = document.querySelectorAll(
                  ".cart-count-bubble span[aria-hidden]," +
                  "[data-cart-count]," +
                  ".cart-count"
                );
                countEls.forEach((el) => (el.textContent = cart.item_count));
              });

            document.dispatchEvent(
              new CustomEvent("cart:updated", { detail: { cart: response } })
            );
          })
          .catch((err) => {
            console.error(err);
            this.showNotification("Something went wrong. Please try again.", "error");
          })
          .finally(() => {
            this.button.classList.remove("loading");
            this.button.removeAttribute("disabled");
          });
      }

      showNotification(message, type) {
        if (!this.errorWrapper) return;
        this.errorWrapper.innerHTML = "";
        const el = document.createElement("div");
        el.className = "bundle-block__notification bundle-block__notification--" + type;
        el.textContent = message;
        this.errorWrapper.appendChild(el);
        setTimeout(() => el.remove(), 4000);
      }
    }
  );
}
